import {ChangeEvent as ReactChangeEvent, FC, useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {AutoResizeTextarea} from "./AutoResizeTextarea";
import {TabState, TabStateButton} from "./TabStateButton";
import {AttribValueInput} from "./AttribValueInput";
import {captureDivsToPNG} from "./PNGUtils";

// URL Query.
const QUERY = new URLSearchParams(window.location.search);

// Screen dimensions.
const SCREEN_WIDTH = 1920;
const SCREEN_HEIGHT = 1080;
const SCREEN_PADDING = 50;
const SCREEN_GAP = 50;
const ATTRIB_COLUMN_COUNT = 2;
const ATTRIB_DISPLAY_WIDTH = (SCREEN_WIDTH - (2 * SCREEN_PADDING) - ((ATTRIB_COLUMN_COUNT - 1) * SCREEN_GAP)) / ATTRIB_COLUMN_COUNT;

// Get current tab state from query.
const CURRENT_TAB_STATE_STORAGE_NAME = "current-tab-state";
const CURRENT_TAB_STATE: TabState = (QUERY.get(CURRENT_TAB_STATE_STORAGE_NAME) as TabState) || TabState.ATTRIB_LIST;

// Get current attrib index from query.
const CURRENT_ATTRIB_INDEX_STORAGE_NAME = "current-attrib-index";
const CURRENT_ATTRIB_INDEX = parseInt(QUERY.get(CURRENT_ATTRIB_INDEX_STORAGE_NAME) || "0", 10);

// Load attribList from local storage.
const ATTRIB_LIST_STORAGE_NAME = "ATTRIB_LIST";
const STORED_ATTRIB_LIST = JSON.parse(localStorage.getItem(ATTRIB_LIST_STORAGE_NAME) || "[]");

// Load attribMap from local storage.
const ATTRIB_MAP_STORAGE_NAME = "ATTRIB_MAP";
const STORED_ATTRIB_MAP = JSON.parse(localStorage.getItem(ATTRIB_MAP_STORAGE_NAME) || "{}");

const AttribListText = styled(AutoResizeTextarea)`
    width: 20em;
    height: 30em;
`;
const AttribDisplayScreenHost = styled.div`
    background: linear-gradient(var(--accent-primary) 0%, var(--accent-secondary) 100%);

    width: auto;
    height: ${SCREEN_HEIGHT}px;

    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    flex-wrap: wrap;

    overflow: auto;

    padding: 0;
`;
const AttribDisplayScreen = styled.div<{ hidden?: boolean }>`
    flex: 0 0 auto;
    background-color: transparent;

    width: ${SCREEN_WIDTH}px;
    height: ${SCREEN_HEIGHT}px;

    display: ${p => p.hidden ? 'none' : 'flex'};
    flex-direction: column;
    align-items: start;
    justify-content: start;
    flex-wrap: wrap;

    overflow: hidden;

    padding: ${SCREEN_PADDING}px;
    gap: ${SCREEN_GAP}px;
`;
const AttribDisplayBase = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;

    width: ${ATTRIB_DISPLAY_WIDTH}px;
`;
const AttribDisplayName = styled.div`
    flex: 0 0 auto;
    background-color: var(--primary-bg);
    color: var(--surface-soft);
    font-family: "Monomakh", serif;
    font-weight: bold;
    font-size: 1.75em;

    padding: 15px;
`;
const AttribDisplayValue = styled.div`
    flex: 1 0 auto;
    background-color: var(--surface-soft);
    color: var(--secondary-text);
    font-size: 1.5em;
    font-weight: bold;

    padding: 15px 15px 15px 30px;
`;

type AttribDisplayProps = {
  name: string;
  value: string;
};

const AttribDisplay: FC<AttribDisplayProps> = ({name, value}) => {
  return (
    <AttribDisplayBase>
      <AttribDisplayName>{name}:</AttribDisplayName>
      <AttribDisplayValue>{value}</AttribDisplayValue>
    </AttribDisplayBase>
  );
}

const AppBase = styled.div``;

export type AppProps = {};

export const App: FC<AppProps> = () => {
  const screenHostRef = useRef<HTMLDivElement>(null);
  const [creatingPNGSequence, setCreatingPNGSequence] = useState<boolean>(false);
  const [currentTabState, setCurrentTabStateInternal] = useState<TabState>(CURRENT_TAB_STATE as TabState);
  const [attribList, setAttribList] = useState<string[]>(STORED_ATTRIB_LIST);
  const [attribMap, setAttribMap] = useState<Record<string, string>>(STORED_ATTRIB_MAP);
  const [currentAttribIndex, setCurrentAttribIndexInternal] = useState<number>(CURRENT_ATTRIB_INDEX);
  const setCurrentTabState = useCallback(
    (tabState: TabState) => {
      setCurrentTabStateInternal(tabState);

      const url = new URL(window.location.href);
      url.searchParams.set(CURRENT_TAB_STATE_STORAGE_NAME, tabState);

      window.history.replaceState({}, "", url.toString());
    },
    []
  );
  const setCurrentAttribIndex = useCallback(
    (index: number) => {
      setCurrentAttribIndexInternal(index);

      const url = new URL(window.location.href);
      url.searchParams.set(CURRENT_ATTRIB_INDEX_STORAGE_NAME, index.toString());

      window.history.replaceState({}, "", url.toString());
    },
    []
  );
  const onAttribTextChange = useCallback(
    ({
       target: {
         value = '',
       }
     }: ReactChangeEvent<HTMLTextAreaElement>) => {
      const attribs = value.split("\n");

      setAttribList(attribs);
    },
    []
  );
  const onSetAttribMapValue = useCallback(
    (attrib: string, value: string) => {
      setAttribMap((prev) => {
        return {
          ...prev,
          [attrib]: value,
        };
      });
    },
    []
  );
  const onIncreaseAttribIndex = useCallback(() => {
    setCurrentAttribIndex(Math.min(
      currentAttribIndex + 1,
      attribList.length - 1
    ));
  }, [attribList, currentAttribIndex]);
  const onDecreaseAttribIndex = useCallback(() => {
    setCurrentAttribIndex(Math.max(
      currentAttribIndex - 1,
      0
    ));
  }, [attribList, currentAttribIndex]);
  const onExportPNGSequence = useCallback(() => {
    const screenHost = screenHostRef.current;

    if (screenHost) {
      setCreatingPNGSequence(true);

      setTimeout(async () => {
        await captureDivsToPNG(screenHost);
        setCreatingPNGSequence(false);
      }, 0);
    }
  }, []);

  useEffect(() => {
    // Save attribList to local storage.
    localStorage.setItem(ATTRIB_LIST_STORAGE_NAME, JSON.stringify(attribList));
  }, [attribList]);

  useEffect(() => {
    // Save attribMap to local storage.
    localStorage.setItem(ATTRIB_MAP_STORAGE_NAME, JSON.stringify(attribMap));
  }, [attribMap]);

  return (
    <AppBase>
      <h1>Roll With It - Character Build Screen Generator</h1>
      <div
        role="group"
      >
        <TabStateButton
          tabState={TabState.ATTRIB_LIST}
          currentTabState={currentTabState}
          setCurrentTabState={setCurrentTabState}
        >
          Attribute List
        </TabStateButton>
        <TabStateButton
          tabState={TabState.ATTRIB_MAP}
          currentTabState={currentTabState}
          setCurrentTabState={setCurrentTabState}
        >
          Attribute Values
        </TabStateButton>
        <TabStateButton
          tabState={TabState.ATTRIB_DISPLAY}
          currentTabState={currentTabState}
          setCurrentTabState={setCurrentTabState}
        >
          Attribute Display
        </TabStateButton>
      </div>
      <div>
        {currentTabState === TabState.ATTRIB_LIST ? (
          <>
            <h3>Create Character Attributes (One Per Line)</h3>
            <AttribListText
              value={attribList.join("\n")}
              onChange={onAttribTextChange}
            ></AttribListText>
          </>
        ) : undefined}
        {currentTabState === TabState.ATTRIB_MAP ? (
          <>
            <h3>Character Attribute Values</h3>
            <div>
              {attribList.map((attrib, index) => {
                const attribValue: string = attribMap[attrib] || "";

                return (
                  <AttribValueInput
                    key={`AttribValueInput:${attrib}:${index}`}
                    name={attrib}
                    value={attribValue}
                    onChange={onSetAttribMapValue}
                  />
                );
              })}
            </div>
          </>
        ) : undefined}
        {currentTabState === TabState.ATTRIB_DISPLAY ? (
          <>
            <h3>Current Attribute Screen</h3>
            <div
              role="group"
              style={{
                width: '20em',
              }}
            >
              <button className="small" onClick={onDecreaseAttribIndex}>-</button>
              <button className="small" onClick={onIncreaseAttribIndex}>+</button>
              <button className="small" onClick={onExportPNGSequence}>Export PNG Sequence</button>
            </div>
            {creatingPNGSequence ? (
              <>
                <progress></progress>
              </>
            ) : undefined}
            <AttribDisplayScreenHost
              ref={screenHostRef}
              style={{
                height: creatingPNGSequence ? 0 : undefined,
              }}
            >
              {attribList.map(
                (attrib, index) => {
                  return (
                    <AttribDisplayScreen
                      key={`AttribDisplayScreen:${attrib}:${index}`}
                      hidden={!creatingPNGSequence && index !== currentAttribIndex}
                    >
                      {attribList.map((attrib2, index2) => {
                        if (index2 <= index) {
                          return (
                            <AttribDisplay
                              key={`AttribDisplay:${attrib2}:${index}`}
                              name={attrib2}
                              value={attribMap[attrib2] || ""}
                            />
                          );
                        } else {
                          return null;
                        }
                      })}
                    </AttribDisplayScreen>
                  );
                }
              )}
            </AttribDisplayScreenHost>
          </>
        ) : undefined}
      </div>
    </AppBase>
  );
};
